import * as React from "react";
import { useMemo, useEffect } from "react";
import { Box } from "@mui/material";
import { useFormContext, useWatch, Controller } from "react-hook-form";
import { CategoryInputsPanel } from "./CategoryInputsPanel";
import { FinancialStatementCategoryItem } from "../domain/entities/financial";
import TriggerFigureCheckbox from "./TriggerFigureCheckbox";
import {
  calcTriggerLimit,
  TRIGGER_CATEGORIES,
  useTriggerFiguresFromService,
} from "../domain/entities/triggers";

interface FlexibleStepProps {
  items: FinancialStatementCategoryItem[];
  namePrefix: "income" | "fixed" | "flexible" | "debts";
  totals: any;
  parentTitle: "Flexible Expenditure";
}

export function FlexibleStep({
  items,
  namePrefix,
  totals,
  parentTitle,
}: Readonly<FlexibleStepProps>) {
  const { control, setValue } = useFormContext();

  const adults = useWatch({ control, name: "aboutYou.adults" }) ?? 0;
  const childrenUnder16 =
    useWatch({ control, name: "aboutYou.childrenUnder16" }) ?? 0;
  const children16to18 =
    useWatch({ control, name: "aboutYou.children16to18" }) ?? 0;

  const triggerCfg = useTriggerFiguresFromService();

  const flexTotalsByName = useMemo(() => {
    const map: Record<string, number> = {};
    for (const c of (totals?.categories ?? [])) {
      if (
          c.name === "Flexible Expenditure" ||
          c.parent === "Flexible Expenditure"
      ) {
        map[c.name] = c.value ?? 0;
      }
    }
    return map;
  }, [totals]);

  const triggerMeta = useMemo(() => {
    return TRIGGER_CATEGORIES.map((cat) => {
      const cfg = triggerCfg.find((t) => t.name === cat);
      const limit = calcTriggerLimit(
        adults,
        childrenUnder16,
        children16to18,
        cfg,
      );
      const total = flexTotalsByName[cat] ?? 0;
      const visible = total > limit;
      return { cat, limit, total, visible };
    });
  }, [triggerCfg, adults, childrenUnder16, children16to18, flexTotalsByName]);

  const triggersAck = useWatch({ control, name: "triggersAck" }) as
    | Record<string, boolean>
    | undefined;
  const visibleCats = triggerMeta.filter((m) => m.visible).map((m) => m.cat);
  const allVisibleChecked =
    visibleCats.length === 0
      ? true
      : visibleCats.every((cat) => !!triggersAck?.[cat]);

  useEffect(() => {
    setValue("triggersAckCompleteFlexible" as any, allVisibleChecked, {
      shouldDirty: false,
      shouldValidate: false,
      shouldTouch: false,
    } as any);
  }, [allVisibleChecked, setValue]);

  return (
    <CategoryInputsPanel
      items={items}
      namePrefix={namePrefix}
      totals={totals}
      parentTitle={parentTitle}
      renderUnderSummary={
        <Box minWidth={280}>
          {triggerMeta.map(({ cat, limit, visible }) =>
            visible ? (
              <Controller
                key={cat}
                name={`triggersAck.${cat}`}
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <TriggerFigureCheckbox
                    id={`${cat.toLowerCase().replaceAll(/\s+/g, "-")}-trigger-figure-checkbox`}
                    expenditureType={cat}
                    triggerSum={limit}
                    checked={!!field.value}
                    setChecked={field.onChange}
                  />
                )}
              />
            ) : null,
          )}
        </Box>
      }
    />
  );
}
